import {
  ErrorHandler,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import type { VendorHubEnvironment } from '@vendorhub/shared/config';

/**
 * Initialise Sentry as early as possible (called from `main.ts` before
 * `bootstrapApplication`). No-ops when `env.sentryDsn` is null, which is the
 * default for development.
 *
 * Pass a stable app identifier (`marketplace`, `admin`, `provider-portal`) so
 * events are filterable per-surface in the Sentry UI.
 */
export function initSentry(env: VendorHubEnvironment, appName: string): void {
  if (!env.sentryDsn) return;

  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.production ? 'production' : 'development',
    release: `vendorhub-${appName}@${buildId()}`,
    sendDefaultPii: false,
    tracesSampleRate: env.production ? 0.1 : 1.0,
    replaysSessionSampleRate: env.production ? 0.05 : 0,
    replaysOnErrorSampleRate: env.production ? 1.0 : 0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    beforeSend(event) {
      // Tag every event with the surface so cross-app dashboards stay readable.
      event.tags = { ...(event.tags ?? {}), app: appName };
      return event;
    },
  });
}

/**
 * Registers Sentry's ErrorHandler and TraceService with Angular DI.
 * Safe to include in every app.config — does nothing unless `initSentry` was
 * called beforehand (Sentry becomes a no-op without an active client).
 */
export function provideSentry(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Eagerly instantiate TraceService so it starts listening to NavigationEnd.
      inject(Sentry.TraceService);
    }),
  ]);
}

/**
 * Ambient `SENTRY_RELEASE` is injected by Sentry's webpack/rollup plugin at
 * build time. Fall back to `'dev'` locally so events still have a release tag.
 */
declare const __VH_BUILD_ID__: string | undefined;
function buildId(): string {
  try {
    return typeof __VH_BUILD_ID__ === 'string' ? __VH_BUILD_ID__ : 'dev';
  } catch {
    return 'dev';
  }
}
